import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';

export interface PageHelpGlobalSearchContext {
  searchedText?: string;
  label: string;
  section?: string;
  description: string;
  navigationPath: readonly string[];
  actionHint?: string;
}

interface PageInsight {
  key: string;
  actionCount: number;
}

type ToastPhase = 'hidden' | 'visible' | 'settling';

const GLOBAL_SEARCH_TOAST_DURATION_MS = 5_000;
const GLOBAL_SEARCH_SETTLE_DURATION_MS = 1_050;

const DEFAULT_PAGE_INSIGHT: PageInsight = { key: 'default', actionCount: 2 };

const PAGE_INSIGHT_KEYS: Record<string, string> = {
  reports: 'reportsShipping',
  'reports/shipping': 'reportsShipping',
  'reports/payments': 'reportsPayments',
  dashboard: 'dashboard',
  'rate-management': 'rateManagement.viewRates',
  'rate-management/view-rates': 'rateManagement.viewRates',
  'rate-management/currencies': 'rateManagement.currencies',
  'rate-management/market-rates': 'rateManagement.marketRates',
  'rate-management/edit-tiers': 'rateManagement.editTiers',
  'rate-management/scheduled': 'rateManagement.scheduled',
  'rate-management/approvals': 'rateManagement.approvals',
  currencies: 'rateManagement.currencies',
  denominations: 'rateManagement.denominations',
  'scheduled-changes': 'rateManagement.scheduled',
  'tier-approvals': 'rateManagement.tierApprovalsPending',
  'tier-approvals/pending': 'rateManagement.tierApprovalsPending',
  'tier-approvals/history': 'rateManagement.tierApprovalsHistory',
  'vault-inventory': 'vaultInventory.inventory',
  'vault-inventory/inventory': 'vaultInventory.inventory',
  'vault-inventory/adjustments': 'vaultInventory.adjustments',
  'vault-inventory/holds': 'vaultInventory.holds',
  'vault-inventory/transactions': 'vaultInventory.transactions',
  'vault-management': 'vaultManagement',
  'supplier-restock': 'supplierRestock.orders',
  'supplier-restock/restock-orders': 'supplierRestock.orders',
  'supplier-restock/suppliers': 'supplierRestock.suppliers',
  partners: 'partners.companies',
  'partners/partners': 'partners.companies',
  'partners/users': 'partners.users',
  'partners/applications': 'partners.companyApplications',
  'partners/applications/companies': 'partners.companyApplications',
  'partners/applications/users': 'partners.userApplications',
  'partners/permissions': 'partners.companyPermissions',
  'partners/permissions/company': 'partners.companyPermissions',
  'partners/permissions/users': 'partners.userPermissions',
  'partners/shipping-fees': 'partners.shippingGlobal',
  'partners/shipping-fees/global': 'partners.shippingGlobal',
  'partners/shipping-fees/fee-rules': 'partners.shippingRules',
  'partners/shipping-fees/company-overrides': 'partners.shippingOverrides',
  'partners/valguard-config': 'partners.valguardConfig',
  'order-management': 'orderManagement.inbox',
  'order-management/inbox': 'orderManagement.inbox',
  'order-management/orders': 'orderManagement.orders',
  'order-management/pending-approvals': 'orderManagement.pendingApprovals',
  'order-management/pending-orders': 'orderManagement.pendingOrders',
  'vault-fulfillment': 'vaultFulfillment.packing',
  'vault-fulfillment/packing': 'vaultFulfillment.packing',
  'vault-fulfillment/counting': 'vaultFulfillment.counting',
  'vault-fulfillment/shipping': 'vaultFulfillment.shippingOutgoing',
  'vault-fulfillment/shipping/outgoing': 'vaultFulfillment.shippingOutgoing',
  'vault-fulfillment/shipping/incoming': 'vaultFulfillment.shippingIncoming',
  'vault-fulfillment/eod-reconciliation': 'vaultFulfillment.eod',
  transactions: 'transactions',
  ledger: 'ledger',
  'notification-management': 'notificationManagement',
  audit: 'audit',
  'email-admin': 'emailAdmin',
  'compliance-dashboard': 'complianceDashboard',
  'user-preferences': 'userPreferences.general',
  'user-preferences/general': 'userPreferences.general',
  'user-preferences/system': 'userPreferences.system',
};

@Component({
  selector: 'app-page-help',
  standalone: true,
  imports: [TranslocoPipe],
  templateUrl: './page-help.html',
  styleUrl: './page-help.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageHelpComponent implements AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private toastTimer: number | null = null;
  private settleTimer: number | null = null;
  private scrollToTopObserver: MutationObserver | null = null;

  readonly route = input<string>('');
  readonly globalSearchContext = input<PageHelpGlobalSearchContext | null>(null);

  protected readonly panelOpen = signal(false);
  protected readonly scrollToTopVisible = signal(false);
  protected readonly toastPhase = signal<ToastPhase>('hidden');

  protected readonly insight = computed(() => {
    const insightKey = PAGE_INSIGHT_KEYS[this.routePath()];
    return insightKey ? { key: insightKey, actionCount: 2 } : DEFAULT_PAGE_INSIGHT;
  });
  protected readonly hasGlobalSearchContext = computed(() => this.globalSearchContext() !== null);

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.clearToastTimers();
      this.scrollToTopObserver?.disconnect();
    });
  }

  ngAfterViewInit(): void {
    const scrollToTopButton = document.querySelector('.scroll-to-top-btn');
    if (!(scrollToTopButton instanceof HTMLElement)) {
      return;
    }

    this.updateScrollToTopVisibility(scrollToTopButton);

    this.scrollToTopObserver = new MutationObserver(() => {
      this.updateScrollToTopVisibility(scrollToTopButton);
    });

    this.scrollToTopObserver.observe(scrollToTopButton, {
      attributeFilter: ['class'],
      attributes: true,
    });
  }

  showGlobalSearchToast(context = this.globalSearchContext()): void {
    if (!context) {
      return;
    }

    this.clearToastTimers();
    this.toastPhase.set('visible');

    this.toastTimer = window.setTimeout(() => {
      this.toastPhase.set('settling');
      this.settleTimer = window.setTimeout(() => {
        this.toastPhase.set('hidden');
        this.settleTimer = null;
      }, GLOBAL_SEARCH_SETTLE_DURATION_MS);
      this.toastTimer = null;
    }, GLOBAL_SEARCH_TOAST_DURATION_MS);
  }

  protected togglePanel(): void {
    this.panelOpen.update((open) => !open);
    if (!this.panelOpen()) {
      return;
    }

    this.clearToastTimers();
    this.toastPhase.set('hidden');
  }

  protected closePanel(): void {
    this.panelOpen.set(false);
  }

  protected dismissToast(): void {
    this.clearToastTimers();
    this.toastPhase.set('hidden');
  }

  protected pathText(path: readonly string[]): string {
    return path.join(' > ');
  }

  protected insightKey(suffix: string): string {
    return `pageHelp.insights.${this.insight().key}.${suffix}`;
  }

  private routePath(): string {
    return (
      this.route()
        .split(/[?#]/)[0]
        .replace(/^\/+|\/+$/g, '') || 'dashboard'
    );
  }

  private clearToastTimers(): void {
    if (this.toastTimer !== null) {
      window.clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }

    if (this.settleTimer !== null) {
      window.clearTimeout(this.settleTimer);
      this.settleTimer = null;
    }
  }

  private updateScrollToTopVisibility(scrollToTopButton: HTMLElement): void {
    this.scrollToTopVisible.set(scrollToTopButton.classList.contains('visible'));
  }
}
